/**
 * The hero's words when every founding place is taken (L7, owner 2026-10-23):
 * a visitor who came by the founding link after the last place went is told
 * so kindly and offered the founding waiting list, with no trial offer.
 *
 *   - "All 40 founding places are taken." (the places are the server's,
 *     founding.PLACES) and "Leave your email, or your Instagram or Facebook
 *     name, and I'll tell you when more places open.";
 *   - one field for an email, an Instagram name or a Facebook name. While the
 *     text is not shaped like an email the form also asks which network the
 *     name is on (Instagram, or Facebook for a visitor from ?from=fb); this is
 *     only which control to show: the server reads the text and decides what
 *     it is (an email, a name, a pasted profile address);
 *   - after sending, the thanks line; a refusal (400) says it does not look
 *     like an email or a name; a failure on our side is reported to Sentry and
 *     says it was reported, with hello@gardengameplan.com and never "try
 *     again"; the server's request limit says its own calm sentence;
 *   - a line on what the contact is used for and how to have it removed;
 *   - the quiet "Or start a free garden" link, which starts a garden as
 *     anyone's (the server makes no founder once the places are full).
 *
 * Drawn by Hero in place of its headline, sub line, button and note, on the
 * same light band in Grow's fixed palette (grow-*), so it reads the same in
 * every theme; the refusal and failure lines are the theme's destructive
 * colour. English only, as the whole landing page is (owner, 2026-10-13 and
 * 2026-10-23): the Spanish entries hold the same English.
 *
 * API: POST /billing/founding-waitlist (api/foundingWaitlist).
 * Words: landing.hero.foundingFull.*.
 *
 * @module FoundingFull
 */

import React, { useId, useState } from 'react';
import axios from 'axios';
import { Button } from '@/shared/components/ui/button';
import { FailureText } from '@/shared/components/ServerFailure';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { isRateLimited, rateLimitedWords, reportServerFailure, referenceLine, supportEmail } from '@/shared/lib/serverFailure';
import { joinReference } from '@/shared/lib/serverFailureCore';
import { joinFoundingWaitlist, type FoundingWaitlistNetwork } from '@/shared/api/foundingWaitlist';
import type { LandingArrival } from '../lib/landingFacts';

/** The headline's look, as the hero's own. */
const HEADLINE = 'max-w-3xl font-grow text-4xl font-extrabold leading-[1.08] tracking-tight [text-wrap:balance] md:text-6xl';

/** The line under the headline, as the hero's own. */
const SUB = 'max-w-xl text-lg md:text-xl';

/** The small notes, in Grow's fixed muted type, as the hero's own. */
const NOTE = 'text-sm text-grow-muted-foreground';

/** Where the form is: being filled in, sent, kept, refused, limited or failed. */
type FormState = 'idle' | 'sending' | 'done' | 'invalid' | 'limited' | 'failed';

/** What the full state is handed by Hero. */
interface FoundingFullProps {
  /** How many founding places there were (the server's founding.PLACES). */
  places: number;
  /** How the visitor came: a visitor from the owner's Facebook link has Facebook picked first. */
  arrival: LandingArrival;
  /** Starts a garden: opens the sign-up, as any visitor's would. */
  onStart: () => void;
}

/**
 * Whether the text is shaped like an email: something@something with a dot after the "@" and no "@" in front. Used
 * only to hide the network choice; the server decides what was sent.
 *
 * @param text - The field as typed.
 * @returns True when it looks like an email address.
 */
function looksLikeEmail(text: string): boolean {
  const at = text.indexOf('@');
  return at > 0 && text.indexOf('.', at) > at + 1;
}

/**
 * The full state: the words, the waiting-list form and the quiet start link.
 *
 * @returns The hero's left column content.
 */
export const FoundingFull: React.FC<FoundingFullProps> = ({ places, arrival, onStart }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const fieldId = useId();
  const [contact, setContact] = useState('');
  const [network, setNetwork] = useState<FoundingWaitlistNetwork>(arrival === 'fb' ? 'facebook' : 'instagram');
  const [state, setState] = useState<FormState>('idle');
  // The reference line of a failed send (E3), when Sentry gave a code.
  const [failureRef, setFailureRef] = useState<string | undefined>(undefined);
  const typed = contact.trim();
  const asksNetwork = typed.length > 0 && !looksLikeEmail(typed);

  /**
   * Sends the field to the waiting list and says what came of it.
   *
   * @param event - The form's submit.
   */
  const send = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!typed || state === 'sending') return;
    setState('sending');
    try {
      await joinFoundingWaitlist({ contact: typed, network, language });
      setState('done');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) setState('invalid');
      else if (isRateLimited(error)) setState('limited');
      else {
        reportServerFailure(error, 'Founding waiting list');
        setFailureRef(referenceLine(error));
        setState('failed');
      }
    }
  };

  return (
    <>
      <h1 className={HEADLINE}>{t('landing.hero.foundingFull.title', { places })}</h1>
      <p className={SUB}>{t('landing.hero.foundingFull.sub')}</p>
      {state === 'done' ? (
        <p role="status" className="max-w-xl text-lg font-semibold text-grow-forest">{t('landing.hero.foundingFull.thanks')}</p>
      ) : (
        <form className="max-w-xl space-y-3" onSubmit={send} noValidate>
          <label htmlFor={fieldId} className="block text-sm font-semibold">{t('landing.hero.foundingFull.field')}</label>
          <input
            id={fieldId}
            type="text"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={contact}
            onChange={(event) => { setContact(event.target.value); if (state !== 'sending') setState('idle'); }}
            placeholder={t('landing.hero.foundingFull.placeholder')}
            aria-invalid={state === 'invalid'}
            className="h-12 w-full rounded-md border border-grow-border bg-grow-card px-3 text-base text-grow-charcoal placeholder:text-grow-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grow-forest"
          />
          {asksNetwork && (
            <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <legend className="sr-only">{t('landing.hero.foundingFull.networkLabel')}</legend>
              <span aria-hidden="true" className="text-grow-muted-foreground">{t('landing.hero.foundingFull.networkLabel')}</span>
              {(['instagram', 'facebook'] as const).map((choice) => (
                <label key={choice} className="inline-flex min-h-[44px] cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name={`${fieldId}-network`}
                    value={choice}
                    checked={network === choice}
                    onChange={() => setNetwork(choice)}
                    className="h-4 w-4 accent-grow-forest"
                  />
                  {choice === 'instagram' ? t('landing.hero.foundingFull.instagram') : t('landing.hero.foundingFull.facebook')}
                </label>
              ))}
            </fieldset>
          )}
          <Button
            type="submit"
            size="lg"
            disabled={!typed || state === 'sending'}
            className="h-12 bg-grow-forest px-8 text-base font-bold text-grow-cream shadow-lg hover:bg-grow-forest/90"
          >
            {state === 'sending' ? t('landing.hero.foundingFull.sending') : t('landing.hero.foundingFull.cta')}
          </Button>
          {state === 'invalid' && <p role="alert" className="text-sm text-destructive">{t('landing.hero.foundingFull.invalid')}</p>}
          {state === 'limited' && <p role="alert" className="text-sm text-destructive"><FailureText text={rateLimitedWords()} /></p>}
          {state === 'failed' && (
            <p role="alert" className="text-sm text-destructive">
              <FailureText text={joinReference(t('landing.hero.foundingFull.failed', { email: supportEmail() }), failureRef)} />
            </p>
          )}
        </form>
      )}
      <p className={NOTE}>
        <FailureText text={t('landing.hero.foundingFull.privacy', { email: supportEmail() })} />
      </p>
      <button
        type="button"
        onClick={onStart}
        className="min-h-[44px] text-sm font-semibold text-grow-forest underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grow-forest"
      >
        {t('landing.hero.foundingFull.start')}
      </button>
    </>
  );
};
